import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Search, Swords, X } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { CustomButton } from '@/components/molecules/CustomButton';
import { PokemonPicker } from '@/components/molecules/PokemonPicker';
import { SelectedPokemonPanel } from '@/components/molecules/SelectedPokemonPanel';
import { EvolutionModal } from '@/components/molecules/EvolutionModal';
import { LearnMoveModal } from '@/components/molecules/LearnMoveModal';
import { Loader } from '@/components/atoms/Loader';
import { useToast } from '@/hooks/useToastContext';
import { getInventory, type InventoryItem } from '@/services/user';
import {
  useSuperBonbon as callUseSuperBonbon,
  type OwnedPokemon,
  type EvolutionData,
  type PendingMoveData,
} from '@/services/pokemon';

const SUPER_BONBON_ID = 37;

const ITEM_TYPE_LABELS: Record<string, string> = {
  pokemon_ball: 'Pokéballs',
  pokemon: 'Objets divers',
  evolution_stone: 'Pierres d\'évolution',
  ticket: '🎫 Tickets & Upgrades',
};

// ─── SuperBonbonModal ─────────────────────────────────────────────────────────

interface SuperBonbonModalProps {
  onClose: () => void;
  onUse: (pokemon: OwnedPokemon) => void;
  isUsing: boolean;
  error: string | null;
}

const SuperBonbonModal = ({ onClose, onUse, isUsing, error }: SuperBonbonModalProps) => {
  const [selected, setSelected] = useState<OwnedPokemon | null>(null);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-page flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl shadow-2xl sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-line-soft bg-surface px-5 py-4">
          <div>
            <h2 className="font-lexend text-content font-semibold">Utiliser un Super Bonbon</h2>
            <p className="text-content/50 mt-0.5 text-xs">Choisir le Pokémon qui gagnera un niveau</p>
          </div>
          <button
            onClick={onClose}
            className="text-content/40 hover:text-content rounded-lg p-1.5 transition-colors hover:bg-raised"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <PokemonPicker
            onSelect={setSelected}
            selectedId={selected?.ownedPokemonId}
            loading={isUsing}
            queryKey="super-bonbon"
          />
        </div>

        {error && (
          <div className="mx-4 rounded-xl bg-red-500/10 px-4 py-3">
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        )}

        <div className="border-t border-line-soft bg-surface p-4">
          <SelectedPokemonPanel
            selected={selected}
            loading={isUsing}
            onConfirm={() => selected && onUse(selected)}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Inventory ────────────────────────────────────────────────────────────────

export const Inventory = () => {
  const [search, setSearch] = useState('');
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => getInventory({ offset: 0, limit: 500 }),
    staleTime: 30_000,
  });

  const allItems = data?.data ?? [];
  const items = search
    ? allItems.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : allItems;
  const total = data?.total ?? 0;

  const grouped = items.reduce<Record<string, InventoryItem[]>>(
    (acc, item) => {
      acc[item.itemType] = acc[item.itemType] ?? [];
      acc[item.itemType].push(item);
      return acc;
    },
    {},
  );

  // ── Super Bonbon state ──
  const [showSuperBonbonModal, setShowSuperBonbonModal] = useState(false);
  const [bonbonError, setBonbonError] = useState<string | null>(null);
  const [selectedForBonbon, setSelectedForBonbon] = useState<OwnedPokemon | null>(null);
  const [pendingEvolution, setPendingEvolution] = useState<EvolutionData | undefined>(undefined);
  const [pendingMove, setPendingMove] = useState<PendingMoveData | undefined>(undefined);

  const { mutate: useSuperBonbon, isPending: isUsing } = useMutation({
    mutationFn: (pokemon: OwnedPokemon) => callUseSuperBonbon(pokemon.ownedPokemonId),
    onSuccess: (result, pokemon) => {
      setBonbonError(null);
      setShowSuperBonbonModal(false);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      showToast('success', `Niveau ${result.newLevel} atteint !`);
      setSelectedForBonbon(pokemon);
      if (result.evolution) {
        setPendingEvolution(result.evolution);
      } else if (result.pendingMove) {
        setPendingMove(result.pendingMove);
      }
    },
    onError: (err: Error) => {
      const msg = err.message;
      if (msg.includes('niveau maximum') || msg.includes('niveau 100')) {
        setBonbonError('Ce Pokémon est déjà au niveau maximum.');
      } else if (msg.includes('Super Bonbon') || msg.includes('bonbon')) {
        setBonbonError("Vous n'avez plus de Super Bonbon.");
      } else {
        setBonbonError(msg);
      }
    },
  });

  return (
    <main className="bg-page min-h-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-5">
        <GenericCard
          title="Inventaire"
          subtitle={data ? `${total} objet${total !== 1 ? 's' : ''}` : 'Tes objets et ressources'}
          actions={
            <Link to="/pokemons">
              <CustomButton
                title="Voir mes Pokémon"
                color="primary"
                size="sm"
                rounded="full"
                icon={<Swords size={14} />}
              />
            </Link>
          }
        >
          <div className="relative mb-4 max-w-xs">
            <Search
              size={14}
              className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un objet..."
              className="text-content placeholder:text-content/40 focus:ring-primary-400 h-9 w-full rounded-xl border border-line-soft bg-raised pr-7 pl-8 text-sm focus:ring-2 focus:outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2 transition-colors"
                aria-label="Effacer"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader size={40} color="#f29602" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <p className="text-content/60 text-sm">Impossible de charger l'inventaire.</p>
              <button
                onClick={() => refetch()}
                className="text-primary-500 hover:text-primary-600 text-sm font-semibold underline"
              >
                Réessayer
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <div className="bg-primary-500/15 flex h-20 w-20 items-center justify-center rounded-2xl">
                <Package size={40} className="text-primary-500" />
              </div>
              <div className="text-center">
                <p className="text-content font-lexend font-semibold">
                  {search ? 'Aucun objet trouvé' : 'Inventaire vide'}
                </p>
                <p className="text-content/50 mt-1 text-sm">
                  {search
                    ? `Aucun objet ne correspond à «\u00A0${search}\u00A0»`
                    : "Tu n'as aucun objet pour le moment."}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {Object.entries(grouped).map(([type, typeItems]) => (
                <div key={type}>
                  <p className="text-content font-lexend mb-3 text-sm font-semibold">
                    {ITEM_TYPE_LABELS[type] ?? type}
                  </p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {typeItems.map((item) => (
                      <div
                        key={item.itemId}
                        className="flex items-center gap-3 rounded-2xl border border-transparent bg-surface p-3 shadow-sm"
                      >
                        <div className="bg-primary-500/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
                          <img
                            src={item.icon}
                            alt={item.name}
                            className="h-9 w-9 object-contain"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-content truncate text-sm font-medium">
                            {item.name}
                          </p>
                          <p className="text-primary-500 font-lexend text-sm font-semibold">
                            ×{item.quantity}
                          </p>
                          {item.itemId === SUPER_BONBON_ID && (
                            <button
                              onClick={() => {
                                setBonbonError(null);
                                setShowSuperBonbonModal(true);
                              }}
                              className="bg-primary-500 hover:bg-primary-600 mt-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-white transition-colors"
                            >
                              Utiliser
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            </div>
          )}
        </GenericCard>
      </div>

      {/* ── Modals ── */}
      {showSuperBonbonModal && (
        <SuperBonbonModal
          onClose={() => setShowSuperBonbonModal(false)}
          onUse={useSuperBonbon}
          isUsing={isUsing}
          error={bonbonError}
        />
      )}

      {pendingEvolution && (
        <EvolutionModal
          evolution={pendingEvolution}
          onDone={() => {
            setPendingEvolution(undefined);
            setSelectedForBonbon(null);
          }}
        />
      )}

      {pendingMove && selectedForBonbon && (
        <LearnMoveModal
          ownedPokemonId={selectedForBonbon.ownedPokemonId}
          pokemonName={selectedForBonbon.pokemon?.realname ?? selectedForBonbon.nickname}
          pendingMove={pendingMove}
          currentAttacks={selectedForBonbon.attacks}
          onDone={() => {
            setPendingMove(undefined);
            setSelectedForBonbon(null);
          }}
        />
      )}
    </main>
  );
};
